import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { finalize } from 'rxjs';
import { AsyncConfirmationDialogComponent } from '../../../../core/components/async-confirmation-dialog/async-confirmation-dialog.component';
import { LinkButtonComponent } from '../../../../core/components/buttons/link/link-button.component';
import { LoadingButtonComponent } from '../../../../core/components/buttons/loading/loading-button.component';
import { FormErrorsComponent } from '../../../../core/components/form-errors/form-errors.component';
import { InputFechaComponent } from '../../../../core/components/inputs/input-fecha/input-fecha.component';
import { InputNumberComponent } from '../../../../core/components/inputs/input-number/input-number.component';
import { InputTextareaComponent } from '../../../../core/components/inputs/input-textarea/input-textarea.component';
import { AG_GRID_DEFAULT_OPTIONS } from '../../../../core/config/aggrid.default.config';
import { MetodosPago } from '../../../../core/enums/metodo-pago.enum';
import { BackendErrorService } from '../../../../core/services/backend-error/backend-error.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { ReplaceEmptyStringsWithNull } from '../../../../core/utils/ReplaceEmptyStringsWithNull';
import { CuentaBancariaService } from '../../../../maestros/services/maestros/cuenta-bancaria/cuenta-bancaria.service';
import { EmpresaFacturadoraService } from '../../../../maestros/services/maestros/empresa-facturadora/empresa-facturadora.service';
import { MetodosPagoService } from '../../../../maestros/services/maestros/metodos-pago.service';
import { CuentaBancaria } from '../../../../models/maestros/cuenta-bancaria.model';
import { EmpresaFacturadora } from '../../../../models/maestros/empresa-facturadora.model';
import { GenericMasterModel } from '../../../../models/maestros/generic.master.model';
import { VentaDtoIn, VentaDtoOut, VentaForm } from '../../../models/venta.model';
import { ContabilidadVentasService } from '../../../services/contabilidad-venta.service';

@Component({
  selector: 'app-venta-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextareaComponent,
    InputNumberComponent,
    MatIconModule,
    LoadingButtonComponent,
    RouterModule,
    InputFechaComponent,
    AsyncConfirmationDialogComponent,
    AgGridAngular,
    FormErrorsComponent,
  ],
  templateUrl: './venta-detail.component.html',
  styleUrl: './venta-detail.component.scss',
})
export class VentaDetailComponent implements OnInit {
  ventaId!: number;
  ventaForm!: ReturnType<typeof this.fb.group<VentaForm>>;
  isEditMode = false;
  isLoading = false;
  ventaData!: VentaDtoOut;
  gridApi!: GridApi;

  mostrarDialogoValidacion = false;
  isLoadingValidar = false;

  mostrarDiaologoEliminar = false;
  isLoadingEliminar = false;

  empresasRefacturadoras: EmpresaFacturadora[] = [];
  cuentasBancarias: CuentaBancaria[] = [];
  metodosPago: GenericMasterModel[] = [];

  nuevoCodigoAlbaran: number = 0;
  isLoadingCambiarCodigoAlbaran = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly service: ContabilidadVentasService,
    private readonly toastService: ToastService,
    private readonly backendErrorService: BackendErrorService,
    private readonly router: Router,
    private readonly empresaFacturadoraService: EmpresaFacturadoraService,
    private readonly cuentaBancariaService: CuentaBancariaService,
    private readonly ventaService: MetodosPagoService
  ) {}

  gridOptions: GridOptions = {
    ...AG_GRID_DEFAULT_OPTIONS,
    onGridReady: (params) => (this.gridApi = params.api),
  };

  get columnDefs(): ColDef[] {
    const baseColumns: ColDef[] = [
      { field: 'id', headerName: 'ID' },
      { field: 'codFactura', headerName: 'Código Factura' },
      { field: 'codAlbaran', headerName: 'Código Albarán' },
      { field: 'codContable', headerName: 'Código Contable' },
      { field: 'clienteNombre', headerName: 'Cliente' },
      { field: 'suministroDireccion', headerName: 'Dirección Suministro' },
      { field: 'cuentaBancaria', headerName: 'Cuenta Bancaria' },
      { field: 'baseImponible', headerName: 'Base Imponible' },
      { field: 'importeTotal', headerName: 'Importe Total' },
      {
        field: '',
        headerName: 'Ver Factura',
        cellRenderer: LinkButtonComponent,
        cellRendererParams(params: any) {
          return {
            link: `/facturas-suministros/${params.data.id}`,
          };
        },
        resizable: false,
        sortable: true,
        filter: false,
        suppressMovable: true,
      },
    ];

    return baseColumns;
  }

  ngOnInit(): void {
    this.ventaId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadVenta();
  }

  private loadVenta(): void {
    this.service.obtenerVentaPorId(this.ventaId).subscribe({
      next: (data) => {
        this.cargarTablasMaestras(data.empresaFacturadoraId!);

        this.ventaData = data;
        this.initForm(data);
      },
    });
  }

  cargarTablasMaestras(empresaFacturadoraId: number): void {
    this.empresaFacturadoraService.setComboEmpresasFacturadoras(this.empresasRefacturadoras);
    this.cuentaBancariaService.setComboCuentasBancariasByEmpresaRefacturadoraAsync(
      this.cuentasBancarias,
      empresaFacturadoraId
    );
    this.ventaService.setComboMetodosPago(this.metodosPago);
  }

  private initForm(data: VentaDtoOut): void {
    this.ventaForm = this.fb.group<VentaForm>({
      id: this.fb.control(data.id ?? null, { nonNullable: true }),
      sepaId: this.fb.control(data.sepaId ?? null),
      empresaFacturadoraId: this.fb.control(data.empresaFacturadoraId ?? null),
      metodoPagoId: this.fb.control(data.metodoPagoId ?? null),
      cuentaBancariaId: this.fb.control(data.cuentaBancariaId ?? null),
      fechaAsientoContable: this.fb.control(data.fechaAsientoContable ?? null, { nonNullable: true }),
      fechaValidacion: this.fb.control(data.fechaValidacion ?? null),
      observaciones: this.fb.control(data.observaciones ?? null),
      codAlbaranInicial: this.fb.control(data.codAlbaranInicial ?? null),
      facturas: this.fb.control(
        data.facturas.map((f) => f.id),
        { nonNullable: true }
      ),
    });

    this.ventaForm.disable();
    this.nuevoCodigoAlbaran = data.codAlbaranInicial ?? 0;
  }

  metodoIsDomiciliacionBancaria(): boolean {
    return this.ventaForm.get('metodoPagoId')?.value === MetodosPago.DOMICILIACION_BANCARIA;
  }

  toggleEdit(): void {
    this.isEditMode = !this.isEditMode;
    this.isEditMode ? this.ventaForm.enable() : this.ventaForm.disable();

    this.ventaForm.get('fechaAsientoContable')?.disable();
    this.ventaForm.get('fechaValidacion')?.disable();
    this.ventaForm.get('empresaFacturadoraId')?.disable();
    this.ventaForm.get('metodoPagoId')?.disable();
    this.ventaForm.get('cuentaBancariaId')?.disable();
  }

  guardarCambios(): void {
    this.isLoading = true;

    const raw = this.ventaForm.getRawValue();
    const cleaned = ReplaceEmptyStringsWithNull.clean(raw);

    this.service.actualizarVenta(cleaned as VentaDtoIn).subscribe({
      next: () => {
        this.toastService.success('Venta actualizada correctamente');
        this.isEditMode = false;
        this.ventaForm.get('fechaAsientoContable')?.setValue(new Date().toISOString().slice(0, 10));
        this.ventaForm.disable();
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        if (err?.error?.errors) {
          this.backendErrorService.handleBackendErrors(err.error.errors, this.ventaForm);
        } else {
          this.toastService.error('Error al actualizar la venta');
        }
      },
    });
  }

  exportarExcel(): void {
    this.service.exportarVentaExcel(this.ventaId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `venta_${this.ventaData.id}_${this.ventaData.fechaAsientoContable}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.error('Error al exportar la venta');
      },
    });
  }

  exportarCsv(): void {
    this.service.exportarVentaCsv(this.ventaId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `venta_${this.ventaData.id}_${this.ventaData.fechaAsientoContable}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.error('Error al exportar la venta');
      },
    });
  }

  cancelarEdicion(): void {
    this.initForm(this.ventaData);
    this.isEditMode = false;
  }

  validarVenta(): void {
    this.isLoadingValidar = true;
    this.service.validarVenta(this.ventaId).subscribe({
      next: () => {
        this.toastService.success('Venta validada correctamente');
        this.ventaData.validado = true;
        this.ventaData.fechaValidacion = new Date().toISOString().slice(0, 10);
        this.ventaForm.get('fechaValidacion')?.setValue(this.ventaData.fechaValidacion);
        this.ventaForm.disable();
      },
      error: (err) => {
        this.toastService.error('Error al validar la venta');
        console.error('Error al validar la venta:', err);
        this.isLoadingValidar = false;
        this.mostrarDialogoValidacion = false;
      },
      complete: () => {
        this.isLoadingValidar = false;
        this.mostrarDialogoValidacion = false;
      },
    });
  }

  eliminarVenta(): void {
    this.isLoadingEliminar = true;
    this.service.eliminarVenta(this.ventaData.id).subscribe({
      next: () => {
        this.toastService.success('Venta eliminada correctamente');
        this.router.navigate(['/contabilidad/ventas']);
      },
      error: (err) => {
        this.toastService.error('Error al eliminar la venta');
        console.error('Error al eliminar la venta:', err);
        this.isLoadingEliminar = false;
        this.mostrarDiaologoEliminar = false;
      },
      complete: () => {
        this.isLoadingEliminar = false;
        this.mostrarDiaologoEliminar = false;
      },
    });
  }

  navigateToSepa(): void {
    if (this.ventaData.sepaId) {
      this.router.navigate(['/contabilidad/sepa/', this.ventaData.sepaId]);
    } else {
      this.toastService.error('Sepa no disponible');
    }
  }

  cambiarCodigoAlbaran(): void {
    this.isLoadingCambiarCodigoAlbaran = true;

    this.service
      .cambiarCodigoAlbaran(this.ventaId, this.nuevoCodigoAlbaran)
      .pipe(
        finalize(() => {
          this.isLoadingCambiarCodigoAlbaran = false;
          this.cerrarModal('modalCambiarCodigoAlbaran');
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success('Código de albarán cambiado correctamente');
          this.loadVenta();
        },
        error: (err) => {
          this.toastService.error('Error al cambiar el código de albarán');
          console.error('Error al cambiar el código de albarán:', err);
        },
      });
  }

  abrirModal(modalSeleccionado: string) {
    const modal = document.getElementById(modalSeleccionado) as HTMLDialogElement;
    if (modal) {
      modal.showModal();
    }
  }

  cerrarModal(modalSeleccionado: string) {
    const modal = document.getElementById(modalSeleccionado) as HTMLDialogElement;
    if (modal) {
      modal.close();
    }
  }
}
